import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  deleteCashbackGamesItem,
  getCashbackGamesListList,
  handleClear,
  handlePaginate,
} from "store/liveOps/cashbackGames/slice";
import { cashbacksListLimits } from "configs/limits";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";
import Pagination from "components/Pagination";
import ActionBtn from "components/buttons/ActionBtn";
import {
  NotificationTypes,
  showNotification,
} from "../../../store/notifications/slice";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";

const CashbackGamesList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [currentLimit, setLimit] = useState<number>(cashbacksListLimits[0]);

  const list = useAppSelector((store) => store["cashback-games"].list);
  const isListFetched = useAppSelector(
    (store) => store["cashback-games"].isListFetched,
  );
  const totalItems = useAppSelector(
    (store) => store["cashback-games"].totalItems,
  );
  const totalPages = useAppSelector(
    (store) => store["cashback-games"].totalPages,
  );
  const activePage = useAppSelector(
    (store) => store["cashback-games"].activePage,
  );

  const processingItemId = useAppSelector(
    (store) => store["cashback-games"].processingItemId,
  );

  const location = useLocation();
  const permissions = usePermissionHelper();
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'cashback-games',
    isListFetched
  });

  useEffect(() => {
    return () => {
      dispatch(handleClear());
    };
  }, []);
  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getCashbackGamesListList({
          limit: currentLimit,
          offset: activePage - 1,
        }),
      );
    }
  }, [isListFetched, activePage, currentLimit]);

  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleOpenEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.cashbackGames}&entityId=${id}&type=deposit`,
    );
    dispatch(openModal({ activeModal: ModalTypes.cashbackGames }));
  };
  const handleOpenCreateModal = () => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.cashbackGames}&type=deposit`,
    );
    dispatch(openModal({ activeModal: ModalTypes.cashbackGames }));
  };

  const handleDeleteItem = async (id: number) => {
    const result = await dispatch(deleteCashbackGamesItem(id ?? 0));
    if (result?.payload === true) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const listToRender =
    list?.map((itm) => ({
      ...itm,
      cashBackType: t(itm?.cashBackType || ""),
    })) ?? [];
  
  const actionProps = permissions?.update
  ? {
      onEdit: (id: number) => handleOpenEditModal(id),
      onView: undefined,
    }
  : {
      onEdit: undefined,
      onView: (id: number) => handleOpenEditModal(id),
    };

  return (
    <div className="page-list-wrapper page-list--wo-filter">
      <div className="page-list-content">
        <div className="page-list-header">
          <div className="page-list-header--box">
            {permissions?.create && (
              <ActionBtn
                txt={t("addCashbackList")}
                handleClick={() => {
                  handleOpenCreateModal();
                }}
              />
            )}
          </div>
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={5} rowsCount={4} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <>
            <div className="game-table-wrap">
              {listToRender.length === 0 && (
                <EmptyState message={t("noCashbackGamesFound")} />
              )}

              {listToRender.length > 0 && (
                <Table
                  data={listToRender}
                  idField="id"
                  hasCheckbox={false}
                  onDelete={(id) => handleDeleteItem(id)}
                  processingId={processingItemId}
                  hasDelete={permissions?.delete}
                  hasEdit={permissions?.update}
                  hasView={permissions?.read && !permissions?.update}
                  hasOptions={permissions?.read || permissions?.update}
                  {...actionProps}
                />
              )}
            </div>

            {listToRender.length > 0 && (
              <Pagination
                handleLimitClick={(e) => {
                  setLimit(e);
                  dispatch(handlePaginate(1));
                }}
                limitsArray={cashbacksListLimits}
                activePage={activePage}
                limit={currentLimit}
                totalItems={totalItems}
                totalPages={totalPages}
                handleNextClick={handleNextClick}
                handlePrevClick={handlePrevClick}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CashbackGamesList;
